import React, { useEffect, useState } from 'react'
import type { GetServerSidePropsContext } from 'next'
import Cookie from 'cookie-universal'
import type { MarketingSection, SingleSessionResult } from '@mc/api'
import { BogoBanner } from '@mc/bogo'
import { APIError } from '@mc/next-api-core'
import { paths } from '@mc/paths'
import { DiscountModal } from '@mc/promos'
import { Pages, TRACK_PROPS, TrackContentType } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { RegularFooter } from 'components/Footer/RegularFooter'
import { CoreNavigation } from 'components/Navigation/consumer'
import { StickyFooter } from 'components/StickyFooter'
import { trackPen7565BackendEV } from 'experiments/pen-7565_v1_promo_banner_200_classes/server'
import { trackPen7584BackendEV } from 'experiments/pen-7584_v1_promo_banner_all_classes/server'
import { trackPen7680BackendEV } from 'experiments/pen-7680_v1_promo_banner_gift_cta/server'
import { trackPen7694BackendEV } from 'experiments/pen-7694_v1_promo_sticky_footer_gift_cta_routing/server'
import {
  getDefaultProduct,
  getFullInstructorBioByCampSlug,
  getSessionBySlug,
  getMarketingRows,
} from 'libs/server/api'
import {
  getV2Categories,
  getGrowthClassCategoriesIds,
  getGrowthClassSubcategoriesIds,
  getPrimaryCategoryAndSubcategoryForGrowthClass,
  PrimaryCategoryAndSubcategory,
} from 'libs/server/api/growth'
import { looksLikeValidSlug } from 'libs/server/api/slug-validation'
import { getPageData } from 'libs/server/page-data'
import { pageErrorHandler } from 'libs/server/page-error-handler'
import { useSprig } from 'libs/sprig'
import { redirect } from 'libs/utils'
import { FooterCTAs } from 'pages/sessions/shared/FooterCTAs'
import { GrowthFooterSection } from 'pages/sessions/shared/GrowthFooterSection/GrowthFooterSection'
import { VideoPlayerModal } from 'pages/sessions/shared/VideoPlayerModal'
import type { DefaultProduct } from 'types/bridgeTypes'
import { CampContent } from './components/CampContent'
import { CampHero } from './components/CampHero/CampHero'
import { HeadTag } from './components/HeadTag'
import { SessionCampLandingPageDataProvider } from './components/SessionsLandingPageDataProvider'
import {
  trackCampOverviewTrailerCTAClick,
  trackCampFooterTrailerCTAClick,
  getVideoExtraTrackingInfo,
  trackCampPageViewed,
} from './events'
import type { Camp, InstructorBioResult } from './types'
import { presentSession } from './utils'

export type SessionCampLandingPageProps = {
  session: SingleSessionResult
  annualPass: DefaultProduct
  instructorBio: InstructorBioResult
  primaryCatAndSubcat: PrimaryCategoryAndSubcategory
  classCategoriesIds: number[]
  classSubcategoriesIds: number[]
  marketingRows: MarketingSection[]
}

export type SessionCampLandingPageData = Omit<
  SessionCampLandingPageProps,
  'primaryCatAndSubcat' | 'classCategoriesIds' | 'classSubcategoriesIds'
> & {
  camp: Camp
}

interface VideoProperty {
  contentId: string
  contentTitle: string
  contentType: TrackContentType
  isOverview: boolean
}

const useTrackCampPageViewed = (
  c: Partial<Camp>,
  annualPass: DefaultProduct,
  primaryCategoryAndSubcategory: PrimaryCategoryAndSubcategory,
  classCategoriesIds: number[],
  classSubcategoriesIds: number[],
) => {
  const { id, slug, title } = c

  useEffect(() => {
    if (id && slug && title && primaryCategoryAndSubcategory && annualPass) {
      trackCampPageViewed({
        id,
        slug,
        title,
        price: annualPass.productLtv?.regular,
        primaryCategoryId: primaryCategoryAndSubcategory?.primaryCategory?.id,
        categoryIds: classCategoriesIds,
        subcategoryIds: classSubcategoriesIds,
        currency: annualPass?.pricing?.currency || 'XX',
      })
    }
  }, [
    annualPass,
    id,
    slug,
    title,
    primaryCategoryAndSubcategory,
    classCategoriesIds,
    classSubcategoriesIds,
  ])
}

const setPageViewedCookie = (slug: string) => {
  const cookies = Cookie()
  const DAY = 60 * 60 * 24

  const existing = cookies.get('all_cm_pages_viewed') ?? []

  const withQuotes = encodeURIComponent(slug)

  const newCookieValue = Array.from(new Set([withQuotes, ...existing]))

  const cookieOptions = {
    maxAge: DAY * 14,
    path: '/',
  }

  cookies.set('all_cm_pages_viewed', newCookieValue, cookieOptions)
  cookies.set('last_cm_page', slug, cookieOptions)
}

export default function SessionCampLandingPage({
  session,
  annualPass,
  instructorBio,
  primaryCatAndSubcat,
  classCategoriesIds,
  classSubcategoriesIds,
  marketingRows,
}: SessionCampLandingPageProps) {
  const [videoPlayerId, setVideoPlayerId] = useState<string | null>(null)
  const [videoPlayerUuid, setVideoPlayerUuid] = useState<string | null>(null)
  const [videoProperty, setVideoProperty] = useState<VideoProperty | null>(null)

  const camp = presentSession(session)

  useTrackCampPageViewed(
    camp,
    annualPass,
    primaryCatAndSubcat,
    classCategoriesIds,
    classSubcategoriesIds,
  )
  useSprig()

  const onOverviewClick = () => {
    setVideoPlayerId(camp.classOverviewTrailer?.brightcoveVideoId ?? null)
    setVideoPlayerUuid(camp.classOverviewTrailer?.mediaUuid ?? null)
    setVideoProperty({
      contentId: `${camp.id}_${
        camp.classOverviewTrailer?.brightcoveVideoId ??
        camp.classOverviewTrailer?.mediaUuid
      }`,
      contentTitle: 'Camp Class Overview Trailer',
      contentType: 'Camp Trailer',
      isOverview: true,
    })
    trackCampOverviewTrailerCTAClick({
      ...camp,
      contentId: `${camp.id}_${
        camp.classOverviewTrailer?.brightcoveVideoId ??
        camp.classOverviewTrailer?.mediaUuid
      }`,
    })
  }

  const onFooterTrailerCTAClick = () => {
    setVideoPlayerId(camp.trailer?.brightcoveVideoId ?? null)
    setVideoPlayerUuid(camp.trailer?.mediaUuid ?? null)
    setVideoProperty({
      contentId: `${camp.id}_${
        camp.trailer?.brightcoveVideoId ?? camp.trailer?.mediaUuid
      }`,
      contentTitle: 'Camp Class Footer Trailer',
      contentType: 'Camp Trailer',
      isOverview: false,
    })
    trackCampFooterTrailerCTAClick({
      ...camp,
      contentId: `${camp.id}_${
        camp.trailer?.brightcoveVideoId ?? camp.trailer?.mediaUuid
      }`,
    })
  }

  const onVideoPlayerClose = () => {
    setVideoPlayerId(null)
    setVideoProperty(null)
  }

  useEffect(() => {
    setPageViewedCookie(camp.slug)
  }, [camp.slug])

  return (
    <TrackingParamsProvider
      value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-sessions' }}
    >
      <SessionCampLandingPageDataProvider
        data={{
          session,
          camp,
          annualPass,
          instructorBio,
          marketingRows,
        }}
      >
        <DiscountModal />
        <BogoBanner isDark />
        <HeadTag />
        <CampHero />
        <CampContent
          camp={camp}
          session={session}
          onOverviewClick={onOverviewClick}
        />
        <FooterCTAs
          title={'sessions.footer.readyToGetStarted'}
          onTrailerClick={onFooterTrailerCTAClick}
          pageType='class-page'
          hasTrailer={
            !!camp.trailer?.brightcoveVideoId || !!camp.trailer?.mediaUuid
          }
        />
        {videoProperty && (videoPlayerId || videoPlayerUuid) && (
          <VideoPlayerModal
            videoPlayerId={videoPlayerId as string}
            mediaUuid={videoPlayerUuid ?? undefined}
            contentId={videoProperty.contentId}
            contentTitle={videoProperty.contentTitle}
            contentType={videoProperty.contentType}
            onClose={onVideoPlayerClose}
            extraTrackingInfo={getVideoExtraTrackingInfo(
              videoProperty.isOverview,
              {
                ...camp,
                contentId: videoProperty.contentId,
              },
            )}
            pageType='class-page'
          />
        )}
        <GrowthFooterSection location='camp' />
        <div className='mc-theme-dark'>
          <StickyFooter />
        </div>
      </SessionCampLandingPageDataProvider>
    </TrackingParamsProvider>
  )
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const {
    query: { slug: campSlug },
  } = ctx

  if (typeof campSlug !== 'string' || !looksLikeValidSlug(campSlug)) {
    return pageErrorHandler(
      new APIError('/jsonapi/v1/camps/[slug]', 400, 'Invalid slug'),
    )
  }

  let session
  try {
    session = await getSessionBySlug(ctx, campSlug as string)
  } catch (error) {
    return pageErrorHandler(error)
  }
  if (session.slug !== campSlug) {
    return redirect(ctx, `/sessions/classes/${session.slug}`, {
      queryExclude: ['slug'],
    })
  }

  await trackPen7565BackendEV(ctx)
  await trackPen7584BackendEV(ctx)
  await trackPen7680BackendEV(ctx)
  await trackPen7694BackendEV(ctx)

  const data = await getPageData({
    criticalPromises: {
      session: Promise.resolve(session),
    },
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      categories: getV2Categories(ctx),
      instructorBio: getFullInstructorBioByCampSlug(ctx, campSlug as string),
      primaryCatAndSubcat: getPrimaryCategoryAndSubcategoryForGrowthClass(
        ctx,
        campSlug as string,
      ),
      classCategoriesIds: getGrowthClassCategoriesIds(ctx, campSlug as string),
      classSubcategoriesIds: getGrowthClassSubcategoriesIds(
        ctx,
        campSlug as string,
      ),
      marketingRows: getMarketingRows(ctx),
    },
  })

  const { props } = data as { props?: { error?: { statusCode: number } } }

  // Content-curation exclusions handle
  if (props?.error?.statusCode === 404) {
    return {
      redirect: {
        destination: paths.root,
        permanent: false,
      },
    }
  }

  return data
}

SessionCampLandingPage.pageSettings = {
  rumLabel: 'SessionsPrePaywallCoursePage',
  analyticsLabel: Pages.SESSION_COURSE_MARKETING_PAGE,
  theme: 'mc-theme-sessions',
  showPromo: true,
  navigation: CoreNavigation,
  footer: RegularFooter,
}
